import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import classNames from "classnames/bind";
import styles from "./DriverHomePage.module.css";
import routes from "~/configs";
import {
  FaClipboardCheck,
  FaCarSide,
  FaHistory,
  FaIdCard,
  FaUser,
  FaArrowLeft,
  FaEnvelope,
  FaSpinner,
} from "react-icons/fa";
import { BASE_AUTH_URL, BASE_DRIVER_URL } from "~/helper/apiService";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";

const cx = classNames.bind(styles);

const DriverHomePage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get vehicleId from URL
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const { showToast } = useContext(ToastContext);

  const { showModal } = useContext(ModalContext);

  // Navigation items
  const navItems = [
    {
      to: routes.driverCheckIn.replace(":id", id),
      label: "Check-in hàng ngày",
      icon: <FaClipboardCheck />,
    },
    {
      to: routes.driverWash.replace(":id", id),
      label: "Rửa xe",
      icon: <FaCarSide />,
    },
  ];

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BASE_AUTH_URL}check-login`, {
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          if (data.user.role !== "driver") {
            showToast("Không có quyền truy cập trang này", "error");
            navigate("/login", { replace: true });
            return;
          }
          localStorage.setItem("user", JSON.stringify(data.user));
          setAuthChecked(true);
        } else {
          localStorage.removeItem("user");
          showToast("Vui lòng đăng nhập lại", "error");
          navigate("/login", { replace: true });
        }
      } catch (error) {
        console.error("Error checking auth:", error);
        localStorage.removeItem("user");
        showToast("Lỗi kết nối: " + error.message, "error");
        navigate("/login", { replace: true });
      }
    };

    checkAuth();
  }, [navigate, showToast]);

  // Fetch vehicle data
  const fetchVehicleData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_DRIVER_URL}vehicles/${id}`, {
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Xe không tồn tại");
        } else if (response.status === 403) {
          throw new Error("Không có quyền truy cập thông tin xe này");
        }
        throw new Error(`Lỗi HTTP! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data.vehicles && data.vehicles.length > 0) {
        setVehicle(data.vehicles[0]); // Take the first vehicle
      } else {
        setError("Không tìm thấy thông tin xe");
      }
    } catch (err) {
      setError(err.message || "Lỗi khi tải thông tin xe");
      showToast(err.message || "Lỗi khi tải thông tin xe", "error");
      console.error("Error fetching vehicle data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authChecked) {
      fetchVehicleData();
    }
  }, [id, authChecked]);

  const handleLogout = async () => {
    try {
      const response = await fetch(`${BASE_AUTH_URL}logout`, {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message, "success");
        navigate("/login");
      } else {
        showToast(data.error || "Đăng xuất thất bại", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    }
  };
  const confirmLogout = () => {
    showModal({
      title: "Xác Nhận Đăng Xuất",
      message: "Bạn có chắc chắn muốn đăng xuất?",
      onConfirm: handleLogout,
      onCancel: null,
    });
  };
  // Render loading state
  if (!authChecked || loading) {
    return (
      <div className={cx("homeContainer")}>
        <div className={cx("loading")}>
          <FaSpinner className={cx("spinner")} />
          Đang tải...
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={cx("homeContainer")}>
        <header className={cx("header")}>
          <h2 className={cx("pageTitle")}>Chi Tiết Tài Xế</h2>
        </header>
        <div className={cx("errorMessage")}>
          {error}
          <button
            onClick={() => fetchVehicleData()}
            className={cx("retryButton")}
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cx("homeContainer")}>
      {/* Header */}
      <header className={cx("header")}>
        <h2 className={cx("pageTitle")}>Chi Tiết Tài Xế</h2>
      </header>

      {/* Vehicle Info */}
      <div className={cx("vehicleInfo")}>
        <h2 className={cx("cvtitle")}>Thông Tin Xe</h2>
        <div className={cx("vehicleCard")}>
          <div className={cx("vehicleRow")}>
            <FaCarSide className={cx("vicon")} />
            <span>
              <strong>Xe:</strong> {vehicle.model}
            </span>
          </div>
          <div className={cx("vehicleRow")}>
            <FaIdCard className={cx("vicon")} />
            <span>
              <strong>Biển số:</strong> {vehicle.license_plate}
            </span>
          </div>
          <div className={cx("vehicleRow")}>
            <FaUser className={cx("vicon")} />
            <span>
              <strong>Lái xe:</strong> {vehicle.driverName}
            </span>
          </div>
          <div className={cx("vehicleRow")}>
            <FaEnvelope className={cx("vicon")} />
            <span>
              <strong>Email:</strong> {vehicle.email}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className={cx("navLinks")}>
        {navItems.map((item, idx) => (
          <Link key={idx} to={item.to} className={cx("navButton")}>
            <span className={cx("icon")}>{item.icon}</span>
            <span className={cx("label")}>{item.label}</span>
          </Link>
        ))}
        <div  className={cx("navButton")} onClick={confirmLogout}>
          <FontAwesomeIcon
            icon={faSignOutAlt}
            style={{ marginRight: "10px" }}
          />
          Đăng Xuất
        </div>
      </div>
    </div>
  );
};

export default DriverHomePage;
